import React, { useEffect, useState } from 'react';
import { Card, Spin, Typography, Divider, Result, Button, DatePicker } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import api from '../../services/api';
import locale from 'antd/es/date-picker/locale/tr_TR';
import { LineChartOutlined, RobotOutlined, MessageOutlined } from '@ant-design/icons';

// AllBrandsMetrics bileşenindeki alt bileşenleri import et
import MonthlyMetrics from '../AllBrandsMetrics/components/MonthlyMetrics';
import DailyMetricsTable from '../AllBrandsMetrics/components/DailyMetricsTable';
import SummaryMonthlyMetricsTable from '../AllBrandsMetrics/components/SummaryMonthlyMetricsTable';
import CampaignsList from '../AllBrandsMetrics/components/CampaignsList';
import MetricChartModal from '../AllBrandsMetrics/components/MetricChartModal';
import BrandAuditHistoryModal from '../AllBrandsMetrics/components/BrandAuditHistoryModal';
import BrandHourlyHeatmap from '../AllBrandsMetrics/components/BrandHourlyHeatmap';
import { prepareGridData } from '../AllBrandsMetrics/utils/tableUtils';

const { Title } = Typography;

// Türkçe dil ayarını yap
dayjs.locale('tr');

// Dönen Grafik buton stili için CSS
const rotatingGradientStyle = `
  @keyframes rotate {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  .design-btn {
    position: relative;
    background-origin: padding-box, border-box;
    background-clip: padding-box, border-box;
    cursor: pointer;
    color: #191919;
    border: solid 2px transparent;
    background-image: linear-gradient(to right, #fff, #fff), 
                      conic-gradient(#ff7a00, #fff500, #00ff85, #00f0ff, #000aff, #8f00ff, #ff005c, #fe0000);
    border-radius: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 12px;
    margin-left: 8px;
    font-size: 12px;
    font-weight: bold;
    transition: all 0.3s ease;
  }

  .design-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  .rotating-gradient {
    background-size: 300% 300%;
    animation: rotate 3s linear infinite;
  }
`;

const SingleBrandMetrics = ({ brandId, brandName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [metricsData, setMetricsData] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [gridData, setGridData] = useState({});
  const [brandCurrency, setBrandCurrency] = useState('TRY');
  const [dailyData, setDailyData] = useState([]);
  const [monthlyMetrics, setMonthlyMetrics] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [chartModalVisible, setChartModalVisible] = useState(false);
  const [auditModalVisible, setAuditModalVisible] = useState(false);
  const [chartModalInitialTab, setChartModalInitialTab] = useState('chart');

  useEffect(() => {
    // Marka verisini yükle
    const loadBrandData = async () => {
      try {
        setLoading(true);

        // Marka bilgilerini yükle
        const brandResponse = await api.get(`/brands/${brandId}`);
        if (brandResponse.data.currency) {
          setBrandCurrency(brandResponse.data.currency);
        }

        // Marka için metrikleri getir
        const metricResponse = await api.get(`/brands/${brandId}/metrics`);
        // Sadece aktif ve görünür metrikleri filtrele
        const activeMetrics = metricResponse.data
          .filter(metric => metric.BrandMetric?.isActive && metric.BrandMetric?.isVisible !== false)
          .sort((a, b) => {
            const orderA = a.BrandMetric?.displayOrder || 0;
            const orderB = b.BrandMetric?.displayOrder || 0;
            return orderA - orderB;
          });

        // DailyMetricsTable'ın beklediği formatta metrics nesnesi oluştur
        setMetrics({
          [brandId]: activeMetrics
        });
        
        // Seçili ay için veri alma
        const startDate = selectedMonth.startOf('month').format('YYYY-MM-DD');
        const endDate = selectedMonth.endOf('month').format('YYYY-MM-DD');
        
        // Günlük verileri getir
        const dailyDataResponse = await api.get('/data', {
          params: {
            brandId: brandId,
            startDate: startDate,
            endDate: endDate
          }
        });
        
        // Günlük veriler ham formatında sakla
        setDailyData(dailyDataResponse.data || []);

        // Aylık metrik verilerini getir
        const monthlyDataResponse = await api.get(`/monthly-metrics/brands/${brandId}`, {
          params: {
            date: selectedMonth.format('YYYY-MM')
          }
        });
        
        // Aylık metrik verilerini işle - isVisible=false olanları filtrele
        const filteredMonthlyMetrics = monthlyDataResponse.data
          .filter(metric => metric.isVisible !== false) // isVisible false olanları hariç tut
          .sort((a, b) => {
            const orderA = a.displayOrder || 0;
            const orderB = b.displayOrder || 0;
            return orderA - orderB;
          });
        
        setMonthlyMetrics(filteredMonthlyMetrics);
        
        // Metrik verilerini oluştur
        const dataObject = {
          [brandId]: {
            brandInfo: brandResponse.data,
            metricsData: {
              dailyData: dailyDataResponse.data || [],
              monthlyData: filteredMonthlyMetrics || []
            },
            // MonthlyMetrics bileşeni için ek veri
            filteredMetrics: filteredMonthlyMetrics || [],
            allMetrics: monthlyDataResponse.data || []
          }
        };
        
        setMetricsData(dataObject);
        
        // prepareGridData fonksiyonu ile grid verilerini düzenle
        const preparedGridData = prepareGridData(
          brandId, 
          dailyDataResponse.data || [], 
          { [brandId]: activeMetrics }, 
          dataObject,
          selectedMonth
        );
        
        // Grid verilerini ayarla (DailyMetricsTable için)
        setGridData({
          [brandId]: preparedGridData
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Marka verileri yüklenirken hata:', error);
        console.error('Hata detayları:', error.response?.data);
        setError('Marka verileri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
        setLoading(false);
      }
    };

    loadBrandData();
  }, [brandId, selectedMonth]);

  // Kampanyaları yükle
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        // Tarihleri belirleme
        const monthStart = selectedMonth.startOf('month').format('YYYY-MM-DD');
        const monthEnd = selectedMonth.endOf('month').format('YYYY-MM-DD');
        
        // API'den yalnızca belirli tarih aralığı için kampanyalar getir
        const response = await api.get('/campaigns', {
          params: {
            startDate: monthStart,
            endDate: monthEnd
          }
        });
        
        setAllCampaigns(response.data);
      } catch (error) {
        console.error('Kampanyalar yüklenirken bir hata oluştu:', error);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, [selectedMonth]);

  // CSS stillerini sayfaya ekle
  useEffect(() => {
    // Stil elementini oluştur
    const styleElement = document.createElement('style');
    styleElement.appendChild(document.createTextNode(rotatingGradientStyle));
    
    // Head'e ekle
    document.head.appendChild(styleElement);
    
    // Cleanup
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Ay değişimi
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Veri Yükleme Hatası"
        subTitle={error}
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            Sayfayı Yenile
          </Button>
        }
      />
    );
  }

  // DailyMetricsTable için veri kontrol et
  const hasDailyData = gridData?.[brandId]?.length > 0;
  
  // MonthlyMetricsTable için veri kontrol et
  const hasMonthlyData = monthlyMetrics?.length > 0;

  return (
    <div style={{ padding: '20px' }}>
      <Card
        bodyStyle={{ backgroundColor: '' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>{brandName}</span>
            <div>
              <DatePicker
                picker="month"
                format="MMMM YYYY"
                allowClear={false}
                value={selectedMonth}
                onChange={handleMonthChange}
                locale={locale}
                style={{ width: '150px' }}
                disabled={loading}
              />
            </div>
          </div>
        }
      >
        <div>
          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 8 }}>{brandName} - {selectedMonth.format('MMMM')}</Title>
            <MonthlyMetrics 
              brandId={brandId} 
              allMetricsData={metricsData}
            />
          </div>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <Title level={5} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span>{brandName} - {selectedMonth.format('MMMM')}</span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => {
                  setChartModalInitialTab('chart');
                  setChartModalVisible(true);
                }}
                title="Metrik Grafiği Göster"
              >
                <LineChartOutlined style={{ marginRight: 5 }} /> 
                <span style={{ display: window.innerWidth < 640 ? 'none' : 'inline' }}>Analyze</span>
                <span style={{ display: window.innerWidth < 640 ? 'inline' : 'none' }}>Analyze</span>
              </button>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => setAuditModalVisible(true)}
                title="Ai Audit Geçmişini Göster"
              >
                <RobotOutlined style={{ marginRight: 5 }} /> 
                <span style={{ display: window.innerWidth < 640 ? 'none' : 'inline' }}>Audit Ai</span>
                <span style={{ display: window.innerWidth < 640 ? 'inline' : 'none' }}>Audit Ai</span>
              </button>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => {
                  setChartModalInitialTab('askAI');
                  setChartModalVisible(true);
                }}
                title="Ask Ai"
              >
                <MessageOutlined style={{ marginRight: 5 }} /> 
                <span style={{ display: window.innerWidth < 640 ? 'none' : 'inline' }}>Ask Ai</span>
                <span style={{ display: window.innerWidth < 640 ? 'inline' : 'none' }}>Ask Ai</span>
              </button>
            </div>
          </Title>
          {hasDailyData ? (
            <DailyMetricsTable 
              brandId={brandId}
              gridData={gridData}
              metrics={metrics}
              allMetricsData={metricsData}
              loading={loading}
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>Bu ay için günlük veri bulunamadı.</p>
            </div>
          )}
          
          {/* Saatlik Isı Haritası */}
          <BrandHourlyHeatmap 
            brandId={brandId}
            selectedMonth={selectedMonth}
            currency={brandCurrency}
          />
          
          {/* Kampanya Listesi */}
          <Divider style={{ margin: '12px 0' }} />
          
          <CampaignsList 
            brandId={brandId}
            selectedMonth={selectedMonth}
            allCampaigns={allCampaigns}
          />

          <Divider style={{ margin: '12px 0' }} />
          
          <Title level={5} style={{ marginBottom: 8 }}>{brandName} - Aylık Özet Metrikler</Title>
          {hasMonthlyData ? (
            <SummaryMonthlyMetricsTable
              brandId={brandId}
              selectedMonth={selectedMonth}
              loading={loading}
              brandCurrency={brandCurrency}
            />
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>Bu ay için aylık özet metrik bulunamadı.</p>
            </div>
          )}
        </div>
      </Card>
      
      {/* Metrik grafiği modalı */}
      <MetricChartModal
        visible={chartModalVisible}
        onCancel={() => {
          setChartModalVisible(false);
          setChartModalInitialTab('chart');
        }}
        brandId={brandId}
        brandName={brandName}
        metrics={metrics}
        allMetricsData={metricsData}
        initialTab={chartModalInitialTab}
      />
      
      {/* Audit geçmişi modalı */}
      <BrandAuditHistoryModal
        visible={auditModalVisible}
        onCancel={() => setAuditModalVisible(false)}
        brand={{ id: brandId, name: brandName }}
      />
    </div>
  );
};

export default SingleBrandMetrics; 